import React, { useState, useEffect, useRef } from 'react';
import { getItems } from '../api';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';

/**
 * Reusable item search component with debounced autocomplete dropdown.
 * 
 * @param {function} onSelect - Called when an item is selected. Receives (item) => void
 * @param {string} placeholder - Input placeholder text
 * @param {string} actionIcon - 'add' (PlusIcon) or 'edit' (PencilIcon)
 * @param {boolean} clearOnSelect - Clear input after selection (default: true)
 * @param {React.ReactNode} rightElement - Optional element to render on the right side of input
 */
export default function ItemSearch({ 
  onSelect, 
  placeholder = '+ Add item (type to search)...', 
  actionIcon = 'add',
  clearOnSelect = true,
  rightElement = null 
}) {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!searchText.trim()) { 
      setSearchResults([]); 
      return; 
    }
    const t = setTimeout(async () => {
      try {
        const items = await getItems(`search=${encodeURIComponent(searchText)}`);
        setSearchResults(items);
      } catch (e) {
        console.error('Search error:', e);
        setSearchResults([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [searchText]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    onSelect(item);
    if (clearOnSelect) {
      setSearchText('');
      setSearchResults([]);
    }
    setShowDropdown(false);
  };

  const Icon = actionIcon === 'edit' ? PencilIcon : PlusIcon;

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex gap-2">
        <input
          type="text"
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        {rightElement}
      </div>
      {showDropdown && searchResults.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {searchResults.map(item => (
            <button
              key={item._id}
              onClick={() => handleSelect(item)}
              className="w-full text-left px-4 py-3 hover:bg-gray-700 flex items-center justify-between border-b border-gray-700 last:border-0"
            >
              <div>
                <span className="font-medium">{item.friendlyName}</span>
                {item.variant && <span className="text-gray-400 text-sm ml-2">({item.variant})</span>}
              </div>
              <Icon className="w-5 h-5 text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
